import type { AssistantMessage } from './types';

/**
 * Persists the assistant conversation for the current tab.
 *
 * `sessionStorage` rather than `localStorage`: the chat should survive closing
 * the drawer and reloading the page, but it is scratch state, not a record, so
 * it should not outlive the tab.
 *
 * Entries are keyed by user id and other users' entries are purged on read, so
 * logging out and back in as someone else on the same tab can never surface the
 * previous user's location names.
 */

const PREFIX = 'securspace.assistantChat.';

/** Keeps stored state well clear of the ~5MB sessionStorage quota. */
const MAX_STORED_MESSAGES = 60;

export interface StoredConversation {
  messages: AssistantMessage[];
  input: string;
}

export const storageKeyFor = (userId: string): string => `${PREFIX}${userId}`;

/** Removes conversations belonging to any other user in this tab. */
const purgeOtherUsers = (keep: string): void => {
  const doomed: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(PREFIX) && key !== keep) doomed.push(key);
  }
  doomed.forEach((key) => sessionStorage.removeItem(key));
};

const isMessage = (value: unknown): value is AssistantMessage => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<AssistantMessage>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.text === 'string' &&
    (candidate.role === 'user' || candidate.role === 'assistant')
  );
};

export const loadConversation = (userId: string): StoredConversation | null => {
  if (!userId) return null;
  const key = storageKeyFor(userId);

  try {
    purgeOtherUsers(key);
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredConversation>;
    const messages = Array.isArray(parsed.messages) ? parsed.messages.filter(isMessage) : [];
    return { messages, input: typeof parsed.input === 'string' ? parsed.input : '' };
  } catch {
    // Corrupt or unavailable storage must never block the chat from opening.
    clearConversation(userId);
    return null;
  }
};

export const saveConversation = (userId: string, data: StoredConversation): void => {
  if (!userId) return;
  try {
    sessionStorage.setItem(
      storageKeyFor(userId),
      JSON.stringify({
        messages: data.messages.slice(-MAX_STORED_MESSAGES),
        input: data.input,
      }),
    );
  } catch {
    // Storage full or disabled (e.g. Safari private mode). Persistence is a
    // convenience, so degrade to an in-memory conversation rather than throwing.
  }
};

export const clearConversation = (userId: string): void => {
  if (!userId) return;
  try {
    sessionStorage.removeItem(storageKeyFor(userId));
  } catch {
    // Nothing useful to do if storage is unavailable.
  }
};

/** Attachments the user could still have acted on when the chat was closed. */
const ACTIONABLE = new Set(['confirm', 'choices', 'draft', 'locations']);

/**
 * Prepares stored messages for display again.
 *
 * The staged CSV and parsed export live in refs, not in storage, so a restored
 * "Save this?" button would have nothing behind it. Every attachment is marked
 * resolved on restore so no dead controls are rendered, and the caller is told
 * whether something was left pending so it can say so plainly.
 */
export const restoreMessages = (
  messages: AssistantMessage[],
): { messages: AssistantMessage[]; hadPendingAction: boolean } => {
  let hadPendingAction = false;

  const restored = messages.map((message) => {
    if (message.attachment && !message.resolved) {
      if (ACTIONABLE.has(message.attachment.kind)) hadPendingAction = true;
      return { ...message, resolved: true };
    }
    return message;
  });

  return { messages: restored, hadPendingAction };
};

/** Highest `m<n>` id in use, so restored ids are never reissued. */
export const highestMessageNumber = (messages: AssistantMessage[]): number =>
  messages.reduce((highest, message) => {
    const match = /^m(\d+)$/.exec(message.id);
    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);
